import { z } from "zod";

/* ===========================================================================
 * US-E18.F1-0194 S03 (@UC9273) — CHUYỂN TIỀN LIÊN SỔ (inter-fund transfer).
 * POST /v1/funds/:id/transfer — dời tiền giữa 2 SỔ CÙNG NHÓM (2 tx ledger atomic:
 * FUND_TRANSFER_OUT sổ nguồn / FUND_TRANSFER_IN sổ đích). Σ CASH toàn nhóm BẤT BIẾN.
 *
 * INVARIANTS:
 *  - B1 tiền BigInt: amountMinor = STRING minor (regex ^\d+$, >0, ≤ trần PG BIGINT). KHÔNG float,
 *    KHÔNG number (JS number mất chính xác > 2^53).
 *  - C5 `.strict()` chặn over-post (fundId nguồn từ :id param; server KHÔNG nhận field lạ).
 *  - ĐƠN-CURRENCY: `currency` client gửi phải khớp currency của CẢ 2 sổ (server re-check; lệch ⇒ 409
 *    INTER_FUND_CURRENCY_MISMATCH — FX đa-currency là E18.F3).
 * ========================================================================= */

/** Số nguyên STRING (B1) — không dấu chấm, không âm, không số mũ. */
const INTEGER_STRING_PATTERN = /^\d+$/;
/** Trần PG signed BIGINT (chống overflow P2020→500). */
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const MEMO_MAX = 500;
const IDEMPOTENCY_KEY_MAX = 200;

export const INTER_FUND_AMOUNT_INVALID_MESSAGE =
  "Số tiền chuyển phải là số nguyên dương (đơn vị nhỏ nhất)";

/** Mã lỗi 409 khi 2 sổ khác đơn vị tiền (hằng export — api + mobile dùng CHUNG, chống lệch chuỗi). */
export const INTER_FUND_CURRENCY_MISMATCH_CODE = "INTER_FUND_CURRENCY_MISMATCH" as const;

/** Tiền STRING minor > 0, ≤ trần BIGINT (B1). */
const amountMinorString = z
  .string()
  .trim()
  .min(1, INTER_FUND_AMOUNT_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, INTER_FUND_AMOUNT_INVALID_MESSAGE)
  .regex(INTEGER_STRING_PATTERN, INTER_FUND_AMOUNT_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, INTER_FUND_AMOUNT_INVALID_MESSAGE);

/**
 * Body POST /v1/funds/:id/transfer (sổ NGUỒN = `:id`).
 * `fromFundId` CỐ Ý VẮNG (lấy từ param — không tin body). `.strict()` chặn over-post.
 */
export const interFundTransferRequestSchema = z
  .object({
    /** Sổ ĐÍCH — PHẢI cùng nhóm với sổ nguồn (khác nhóm ⇒ 404 anti-IDOR). */
    toFundId: z.string().uuid(),
    amountMinor: amountMinorString,
    /** Đơn vị tiền — server re-check khớp CẢ 2 sổ (lệch ⇒ 409). */
    currency: z.string().trim().regex(/^[A-Z]{3}$/, "currency phải là mã ISO 3 ký tự hoa"),
    /** Bắt buộc — scope idempotency (double-submit ⇒ đúng 1 CẶP tx, không double-transfer). */
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
    memo: z.string().trim().max(MEMO_MAX).optional(),
  })
  .strict();

export type InterFundTransferRequest = z.infer<typeof interFundTransferRequestSchema>;

/** Kết quả: 2 tx ledger LIÊN KẾT (trỏ nhau qua linkedTxId) + số dư CASH 2 sổ SAU chuyển. */
export const interFundTransferResponseSchema = z
  .object({
    outTxId: z.string().uuid(),
    inTxId: z.string().uuid(),
    fromFundId: z.string().uuid(),
    toFundId: z.string().uuid(),
    amountMinor: z.string().regex(INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /** Số dư CASH sổ nguồn SAU chuyển (STRING minor — B1). */
    fromBalanceMinor: z.string().regex(/^-?\d+$/),
    /** Số dư CASH sổ đích SAU chuyển (STRING minor — B1). */
    toBalanceMinor: z.string().regex(/^-?\d+$/),
    transferredAt: z.string().datetime(),
  })
  .strict();

export type InterFundTransferResponse = z.infer<typeof interFundTransferResponseSchema>;

/* ---------------------------------------------------------------------------
 * S04 — `Fund.settings.role` metadata (@UC9274): nhãn vai trò sổ (reserve/event/operating).
 * CHỈ metadata hiển thị/phân loại — KHÔNG khoá nghiệp vụ (không chặn chuyển/chi).
 * ------------------------------------------------------------------------- */

export const FUND_ROLE = {
  RESERVE: "reserve",
  EVENT: "event",
  OPERATING: "operating",
} as const;

export type FundRole = (typeof FUND_ROLE)[keyof typeof FUND_ROLE];

export const fundRoleSchema = z.enum([FUND_ROLE.RESERVE, FUND_ROLE.EVENT, FUND_ROLE.OPERATING]);

/** PATCH /v1/funds/:id/role — ghi nhãn vai trò sổ (owner/admin/treasurer). */
export const updateFundRoleRequestSchema = z.object({ role: fundRoleSchema }).strict();

export type UpdateFundRoleRequest = z.infer<typeof updateFundRoleRequestSchema>;

export const fundRoleResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    /** null = sổ cũ chưa gắn nhãn (back-compat settings null). */
    role: fundRoleSchema.nullable(),
  })
  .strict();

export type FundRoleResponse = z.infer<typeof fundRoleResponseSchema>;
